import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VenueDetailsService } from '../../services/venue-details.service';
import { NotifierService } from 'src/app/services/notifier.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Interface for HR Exporter Connector
interface HRExporterConnector {
  id?: string;
  type: 'TCPServer' | 'TCPClient' | 'UDPClient';
  protocol: 'MQTT' | 'DATA' | 'REQUESTS' | 'MONITOR' | 'CONTROL';
  hostname: string;
  port: number;
  enabled: boolean;
  groupId?: string;
  isNew?: boolean;
  isUpdated?: boolean;
  isDeleted?: boolean;

  // API response fields
  Id?: number;
  ConnectorGroupId?: number;
  Type?: string;
  Protocol?: string;
  Hostname?: string;
  Port?: number;
  Enabled?: boolean;
}

interface ConfirmModalData {
  title: string;
  message: string;
  confirmText?: string;
  onConfirm: () => void;
}

@Component({
  selector: 'app-connector-group-management',
  templateUrl: './connector-group-management.component.html',
  styleUrls: ['./connector-group-management.component.css']
})
export class ConnectorGroupManagementComponent implements OnInit {
  groupId: string = '';
  connectors: HRExporterConnector[] = [];
  isLoadingConnectors = false;
  connectorForm: FormGroup;
  showConnectorModal = false;
  editMode = false;
  currentConnectorId = '';
  config: any;
  showConfirmModal = false;
  confirmModalData: ConfirmModalData = {
    title: '',
    message: '',
    onConfirm: () => { }
  };
  connectorProtocolOptions = ['MQTT', 'DATA', 'REQUESTS', 'MONITOR', 'CONTROL'];
  connectorTypeOptions = ['TCPServer', 'TCPClient', 'UDPClient'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private venueDetailsService: VenueDetailsService,
    private notifier: NotifierService,
    private fb: FormBuilder
  ) {
    this.config = this.route.snapshot.data['config'];
    this.connectorForm = this.fb.group({
      type: ['TCPServer', Validators.required],
      protocol: ['MQTT', Validators.required],
      hostname: ['', Validators.required],
      port: ['', [Validators.required, Validators.min(0), Validators.max(65535)]],
      enabled: [true]
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.groupId = params['group_id'];
      this.loadConnectors(this.groupId, this.config);
    });
  }

  async loadConnectors(groupId: string, config: any): Promise<void> {
    if (!groupId) return;

    this.isLoadingConnectors = true;
    try {
      const response = await this.venueDetailsService.getConnectors(config, groupId);
      if (response && response.data) {
        this.connectors = response.data.map((connector: any) => ({
          id: connector.Id?.toString(),
          type: connector.Type,
          protocol: connector.Protocol,
          hostname: connector.Hostname,
          port: connector.Port,
          enabled: connector.Enabled,
          groupId: connector.ConnectorGroupId?.toString() || groupId,
          Id: connector.Id,
          ConnectorGroupId: connector.ConnectorGroupId,
          Type: connector.Type,
          Protocol: connector.Protocol,
          Hostname: connector.Hostname,
          Port: connector.Port,
          Enabled: connector.Enabled
        }));

        console.log('Loaded connectors:', this.connectors);
      } else {
        this.connectors = [];
      }
    } catch (err) {
      this.notifier.alert('Error', '', 'Failed to load connectors.', 'error');
      console.error('Error loading connectors:', err);
    } finally {
      this.isLoadingConnectors = false;
    }
  }

  goBack(): void {
    this.router.navigate(['/dash/settings/connector-management']);
  }

  openConnectorModal(connector?: HRExporterConnector): void {
    this.editMode = !!connector;
    this.currentConnectorId = connector?.id || connector?.Id?.toString() || '';

    if (connector) {
      this.connectorForm.setValue({
        type: connector.Type || connector.type || 'TCPServer',
        protocol: connector.Protocol || connector.protocol || 'MQTT',
        hostname: connector.Hostname || connector.hostname || '',
        port: connector.Port || connector.port || '',
        enabled: connector.Enabled !== undefined ? connector.Enabled : (connector.enabled || true)
      });
    } else {
      this.connectorForm.reset({
        type: 'TCPServer',
        protocol: 'MQTT',
        hostname: '',
        port: '',
        enabled: true
      });
    }

    this.showConnectorModal = true;
  }

  closeConnectorModal(): void {
    this.showConnectorModal = false;
  }

  async saveConnector(): Promise<void> {
    if (this.connectorForm.invalid) {
      this.notifier.alert('Error', '', 'Please fill in all required fields.', 'error');
      return;
    }

    const action = this.editMode ? 'update' : 'create';
    this.openConfirmModal({
      title: `${this.editMode ? 'Update' : 'Create'} Connector`,
      message: `Are you sure you want to ${action} this connector?`,
      confirmText: this.editMode ? 'Update' : 'Create',
      onConfirm: async () => {
        try {
          const formValue = this.connectorForm.value;
          const payload: any = {
            action: this.editMode ? 'update_connector' : 'create_connector',
            data: {
              groupId: this.groupId,
              type: formValue.type,
              protocol: formValue.protocol,
              hostname: formValue.hostname,
              port: parseInt(formValue.port, 10),
              enabled: formValue.enabled
            }
          };

          if (this.editMode && this.currentConnectorId) {
            payload.data.id = this.currentConnectorId;
          }

          await this.venueDetailsService.processConnector(this.config, payload);
          this.notifier.alert('Success', '', `Connector ${this.editMode ? 'updated' : 'created'} successfully.`, 'success');
          this.closeConnectorModal();
          this.loadConnectors(this.groupId, this.config);
        } catch (err) {
          this.notifier.alert('Error', '', `Failed to ${this.editMode ? 'update' : 'create'} connector.`, 'error');
          console.error(`Error ${this.editMode ? 'updating' : 'creating'} connector:`, err);
        } finally {
          this.closeConfirmModal();
          this.loadConnectors(this.groupId, this.config);
        }
      }
    });
  }

  deleteConnector(connectorId: string): void {
    this.openConfirmModal({
      title: 'Delete Connector',
      message: 'Are you sure you want to delete this connector?',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await this.venueDetailsService.deleteConnector(this.config, connectorId);
          this.notifier.alert('Success', '', 'Connector deleted successfully.', 'success');
          this.loadConnectors(this.groupId, this.config);
        } catch (err) {
          this.notifier.alert('Error', '', 'Failed to delete connector.', 'error');
          console.error('Error deleting connector:', err);
        } finally {
          this.closeConfirmModal();
          this.loadConnectors(this.groupId, this.config);
        }
      }
    });
  }

  toggleConnectorStatus(connector: HRExporterConnector): void {
    const currentEnabled = connector.Enabled !== undefined ? connector.Enabled : connector.enabled;
    const action = currentEnabled ? 'disable' : 'enable';

    this.openConfirmModal({
      title: `${currentEnabled ? 'Disable' : 'Enable'} Connector`,
      message: `Are you sure you want to ${action} this connector?`,
      confirmText: currentEnabled ? 'Disable' : 'Enable',
      onConfirm: async () => {
        try {
          await this.venueDetailsService.updateConnector(
            this.config,
            connector.Id?.toString() || connector.id || '',
            connector.ConnectorGroupId?.toString() || connector.groupId || this.groupId,
            connector.Type || connector.type,
            connector.Protocol || connector.protocol,
            connector.Hostname || connector.hostname,
            (connector.Port || connector.port || 0).toString(),
            !currentEnabled
          );

          this.notifier.alert('Success', '', `Connector ${!currentEnabled ? 'enabled' : 'disabled'} successfully.`, 'success');
          this.loadConnectors(this.groupId, this.config);
        } catch (err) {
          this.notifier.alert('Error', '', 'Failed to update connector status.', 'error');
          console.error('Error updating connector status:', err);
        } finally {
          this.closeConfirmModal();
          this.loadConnectors(this.groupId, this.config);
        }
      }
    });
  }

  openConfirmModal(data: ConfirmModalData): void {
    this.confirmModalData = data;
    this.showConfirmModal = true;
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }
}
